import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Skeleton,
    useMediaQuery,
    useTheme,
    Tooltip,
    IconButton,
  } from "@mui/material";
  import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetOrganizationProfileMutation } from "Services/OrganizationProfileService";
import { useGetCountryListQuery, useGetForeignStatesMutation, useGetCountiesByStateMutation, useGetCityListByProvinceOrStatesMutation } from "Services/CountryService";
import { handleChange, handleGet, handleChangeCountry, handleChangeProvinceOrState, handleChangeCounty, handleChangeCity, handleFillCountryAndState, setForeignStatesList, setCountyList, setCityList } from "./OrganizationProfileInfo.Slice";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import FormControlWrapper from "components/FormControlWrapper";
import AutocompleteWrapper from "components/AutocompleteWrapper";
import SelectWrapper from "components/SelectWrapper";
import MaskedInputWrapper from "components/MaskedInputWapper";
import { usePrompt } from "react-router-dom";
import { useState } from "react";
import AdditionalInformationPopup from "./additional-informations/AdditionalInformation.Popup"
import OnboardingPopup from "views/shared/OnboardingPopup";
import SubCard from "ui-component/cards/SubCard";

const OrganizationProfileInfoPage = () => {

    const {
        countryList,
        companyName,
        phoneNumber,
        businessEin,
        addressLine,
        addressLine2,
        city,
        provinceOrState,
        country,
        county,
        postalCode,
        selectedCountry,
        selectedProvinceOrState,
        currentProfileInfo,
        foreignStatesList,
        countyList,
        cityList
    } = useSelector((state) => state.organizationProfileState);

    const theme = useTheme();
    const dispatch = useDispatch();
    const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();
    const [getOrganizationProfile, { data, isLoading: isLoadingOrganizationProfile }] = useGetOrganizationProfileMutation();
    const { isLoading: isLoadingCountry } = useGetCountryListQuery();
    const [getForeignStates, { isLoading: isLoadingForeignStates }] = useGetForeignStatesMutation();
    const [getCountiesByState, { isLoading: isLoadingCounties }] = useGetCountiesByStateMutation();
    const [getCityListByProvinceOrStates, { isLoading: isLoadingCities }] = useGetCityListByProvinceOrStatesMutation();
    const isUsOrganization = country.value === "United States";
    const [openPopupAdditionalInformation, setOpenPopupAdditionalInformation] = useState(false);

    // Read fresh inside the city-list response handler below, instead of the closure's
    // possibly-stale value, since the request resolves after the component may have re-rendered.
    const cityValueRef = useRef(city.value);
    cityValueRef.current = city.value;

    const { userToken } = useSelector(state => state.sharedState);
    let userRoles = [];

    if (Array.isArray(userToken?.role)) {
        userRoles = userToken?.role ?? ['User'];
    }
    else {
        userRoles.push(userToken?.role ?? 'User');
    }
    
    const isAdmin = userRoles.filter((string) =>
        string.toUpperCase().includes("Admin".toUpperCase()) ||
        string.toUpperCase().includes("SuperAdmin".toUpperCase())
    ).length > 0;

    usePrompt(
        "You have unsaved changes. Are you sure to discard all changes made?",
        companyName.value !== currentProfileInfo.companyName
        || phoneNumber.value !== currentProfileInfo.phoneNumber
        || businessEin.value !== (currentProfileInfo.businessEin ?? "")
        || addressLine.value !== currentProfileInfo.addressLine
        || addressLine2.value !== currentProfileInfo.addressLine2
        || city.value !== currentProfileInfo.city
        || provinceOrState.value !== currentProfileInfo.provinceOrState
        || country.value !== currentProfileInfo.country
        || county.value !== (currentProfileInfo.county ?? "")
        || postalCode.value !== currentProfileInfo.postalCode
    );
    const handleOpenPopup = () => {
        setOpenPopupAdditionalInformation(true);
      };

    const handleClosePopupAdditionalInformation = () => {
        setOpenPopupAdditionalInformation(false);
      };

    const handleGetOrganizationProfile = async() => {
        const response = await getOrganizationProfile().unwrap();
        dispatch(handleGet(response));
    };

    const handleGetForeignStates = (countryCode, callback = null) => {
        getForeignStates(countryCode).unwrap().then(response => {
            dispatch(setForeignStatesList(response));
            if (callback) {
                callback(response);
            }
        });
    }

    const handleGetCounties = (stateCode) => {
        getCountiesByState(stateCode).unwrap().then(response => {
            dispatch(setCountyList(response));
        });
    }

    const handleGetCities = (stateCode) => {
        getCityListByProvinceOrStates([stateCode]).unwrap().then(response => {
            dispatch(setCityList(response));

            const currentCity = cityValueRef.current;
            const isCurrentCityValid = response.some(o => o.name?.toLowerCase() === currentCity?.toLowerCase());

            if (currentCity && !isCurrentCityValid) {
                dispatch(handleChange({ field: "city", value: "" }));
            }
        });
    }

    useEffect(() => {
        handleGetOrganizationProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    useEffect(() => {
        if (!isLoadingCountry && !isLoadingOrganizationProfile) {
            if (data) {
                const selectedCountry = countryList.find(o => o.description === data.country);

                if (selectedCountry) {
                    handleGetForeignStates(selectedCountry.code, stateList => {
                        dispatch(handleFillCountryAndState(stateList));
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingOrganizationProfile, isLoadingCountry]);

    useEffect(() => {
        if (isUsOrganization && selectedProvinceOrState.code) {
            handleGetCounties(selectedProvinceOrState.code);
        } else {
            dispatch(setCountyList([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUsOrganization, selectedProvinceOrState.code]);

    useEffect(() => {
        if (selectedProvinceOrState.code) {
            handleGetCities(selectedProvinceOrState.code);
        } else {
            dispatch(setCityList([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProvinceOrState.code]);

    return (
        <>
            {isLoadingOrganizationProfile 
            ?  
            <Grid container>
                <Grid item xs={12} sm={12} md={12}>
                    <Skeleton height={200} />
                </Grid>
            </Grid>
            :
            <div>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SubCard title="Basic Information">
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <FormControlWrapper
                                    name="companyName"
                                    value={companyName.value}
                                    label={t('labels.company_organization')}
                                    type="text"
                                    maxLength="200"
                                    error={companyName.error}
                                    theme={theme}
                                    onChange={(element) => dispatch(handleChange({ field: "companyName", value: element.target.value }))}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <MaskedInputWrapper
                                    name="phoneNumber"
                                    value={phoneNumber.value}
                                    label={t('labels.phone')}
                                    onChange={(element) => dispatch(handleChange({ field: "phoneNumber", value: element.target.value }))}
                                    maskType="phone"
                                    error={phoneNumber.error}
                                    theme={theme}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControlWrapper
                                    name="businessEin"
                                    value={businessEin.value}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Business/EIN
                                            <Tooltip title={t("tooltips.business_ein")} arrow placement="top">
                                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                    onChange={(element) => dispatch(handleChange({ field: "businessEin", value: element.target.value }))}
                                    error={businessEin.error}
                                    theme={theme}
                                    placeholder={`(${country.value === "Canada" ? "CA" : "US"}) e.g., ${country.value === "Canada" ? "123456789RR0001" : "12-3456789"}`}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>

                <Grid item xs={12}>
                    <SubCard title="Address">
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                                <SelectWrapper
                                    name="country"
                                    value={country.value}
                                    label={t('labels.country')}
                                    error={country.error}
                                    required
                                    disabled
                                    theme={theme}
                                    loading={isLoadingCountry}
                                    data={countryList.map(country => ({
                                        key: country.id,
                                        value: country.description,
                                        text: country.description,
                                        extraInfo: country.code
                                    }))}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} alignItems="center" sx={{ mt: 0 }}>
                            <Grid item xs={12} sm={6}>
                                <FormControlWrapper
                                    name="addressLine"
                                    value={addressLine.value}
                                    label={t('labels.address')}
                                    type="text"
                                    maxLength="200"
                                    error={addressLine.error}
                                    theme={theme}
                                    onChange={(element) => dispatch(handleChange({ field: "addressLine", value: element.target.value }))}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControlWrapper
                                    name="addressLine2"
                                    value={addressLine2.value}
                                    label={t('labels.complementary_address')}
                                    type="text"
                                    maxLength="200"
                                    error={addressLine2.error}
                                    theme={theme}
                                    onChange={(element) => dispatch(handleChange({ field: "addressLine2", value: element.target.value }))}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} alignItems="center" sx={{ mt: 0 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <AutocompleteWrapper
                                    id="provinceOrState"
                                    label="State"
                                    value={selectedProvinceOrState.description ?? ''}
                                    onChange={(event, newValue) => dispatch(handleChangeProvinceOrState(newValue))}
                                    theme={theme}
                                    error={provinceOrState.error}
                                    options={foreignStatesList.map(state => ({
                                        id: state.id,
                                        value: state.description,
                                        label: state.description
                                    }))}
                                />
                            </Grid>

                            {isUsOrganization &&
                                <Grid item xs={12} sm={6} md={3}>
                                    <AutocompleteWrapper
                                        id="county"
                                        label={t('labels.county')}
                                        value={county.value ?? ''}
                                        onChange={(event, newValue) => dispatch(handleChangeCounty(newValue?.value ?? ''))}
                                        theme={theme}
                                        error={county.error}
                                        loading={isLoadingCounties}
                                        options={countyList.map(countyName => ({
                                            id: countyName,
                                            value: countyName,
                                            label: countyName
                                        }))}
                                    />
                                </Grid>
                            }

                            <Grid item xs={12} sm={6} md={3}>
                                <AutocompleteWrapper
                                    id="city"
                                    label={t('labels.city')}
                                    value={city.value ?? ''}
                                    onChange={(event, newValue) => dispatch(handleChangeCity(newValue?.value ?? ''))}
                                    theme={theme}
                                    error={city.error}
                                    loading={isLoadingCities}
                                    options={cityList.map(cityOption => ({
                                        id: cityOption.id,
                                        value: cityOption.name,
                                        label: cityOption.name
                                    }))}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                               <FormControlWrapper
                                    name="postalCode"
                                    value={postalCode.value}
                                    label={isUsOrganization ? t('labels.zip') : t('labels.postal')}
                                    type="text"
                                    maxLength="20"
                                    error={postalCode.error}
                                    theme={theme}
                                    onChange={(element) => dispatch(handleChange({ field: "postalCode", value: element.target.value }))}
                                    required
                                />
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
            </Grid>

            <Grid item>
{/*
                     <AdditionalInformationPopup
                        openPopup={openPopupAdditionalInformation}
                        onClose={handleClosePopupAdditionalInformation}

                    ></AdditionalInformationPopup> */}
                <OnboardingPopup
                    open={openPopupAdditionalInformation}
                    onClose={handleClosePopupAdditionalInformation}
                />
            </Grid>
            </div>
            }
        </>
    )

};


export default OrganizationProfileInfoPage;
